/* eslint-disable */
import { Point } from './PointHelpers';
import { Size } from './SizeHelpers';

const getPointers = e => e.pointers.map(({ screenX: x, screenY: y }) => ({ x, y }));
const truncRound = num => Math.trunc(num * 10000) / 10000;
const SWIPE_VELOCITY = 0.6;
const minZoomScale = 1;
const maxZoomScale = 2;

/**
 * Responsible for tracking and handling all sorts of gestures in ZoomedGallery:
 * Slider events:
 * - pan is handled to perform scroll + sticky behavior on mobile
 * - doubletap: if current zoom scale value is 1 then zoom image to maxZoomScale value, set it to 1 otherwise.
 * - singletap: close ZoomedGallery
 * Slides events are basicly allow user to pinch to zoom and interact with it by panning it.
 */
export default class GestureManager {
    constructor() {
        this.pinchStartZoomFactor = 1.0;
        this.pinchStartTranslate = Point.newOriginPoint();
        this.pinchStartTouchPointDist = 0;

        this.panStartPoint = Point.newOriginPoint();
        this.panStartTranslate = Point.newOriginPoint();
        this.hammerInstances = [];
        this.thumbnails = [];

        this.transform = {
            zoomFactor: 1.0,
            translate: { x: 0, y: 0 },
        };
    }

    init = () => {
        import('hammerjs').then((Hammer) => {
            this.Hammer = Hammer.default;
        });
    };

    setNewIndex = (index) => {
        if (this.index !== index) {
            this.setTransform({
                zoomFactor: minZoomScale,
                translate: { x: 0, y: 0 },
            });
            this.index = index;
        }
        if (this.thumbnails[index]) {
            this.thumbnails.forEach(
                node => node && node.classList.remove('active'),
            );
            this.thumbnails[index].classList.add('active');
        }
    }

    startListening = ({ refs: { slider, slides, thumbnails }, index, onClose }) => {
        this.slides = slides;
        this.slider = slider;
        this.thumbnails = thumbnails;
        this.index = index;
        this.onClose = onClose;
        if (!this.Hammer) return;
        this.sliderHummerInstance = new this.Hammer.Manager(this.slider);
        this.sliderHummerInstance.add(
            new this.Hammer.Tap({
                event: 'doubletap',
                taps: 2,
                threshold: 20,
                posThreshold: 40,
            }),
        );
        this.sliderHummerInstance.add(
            new this.Hammer.Tap({ event: 'singletap' }),
        );
        this.sliderHummerInstance.add(new this.Hammer.Pan({ event: 'pan' }));
        this.sliderHummerInstance.get('doubletap').recognizeWith('singletap');
        this.sliderHummerInstance.get('singletap').requireFailure('doubletap');
        this.sliderHummerInstance.on('singletap doubletap', this.onSliderTap);
        this.sliderHummerInstance.on('pan', this.onSliderPan);
        this.slides.forEach((ref, idx) => {
            this.hammerInstances[idx] = new this.Hammer(ref);
            this.hammerInstances[idx].get('pinch').set({ enable: true });
            this.hammerInstances[idx].on('pinchstart', this.onPinchStart);
            this.hammerInstances[idx].on('pinchmove', this.onPinchMove);
            this.hammerInstances[idx].on('pinchend', this.onPinchEnd);
            this.hammerInstances[idx].on('panstart', this.onPanStart);
            this.hammerInstances[idx].on('panmove', this.onPanMove);
            this.hammerInstances[idx].on('panend', this.onPanEnd);
            this.hammerInstances[idx].on('doubletap', this.onPanEnd);
        });
    };

    stopListening = () => {
        [this.sliderHummerInstance, ...this.hammerInstances].forEach(
            instance => instance
                && typeof instance.destroy === 'function'
                && instance.destroy(),
        );
        this.setTransform({
            zoomFactor: 1.0,
            translate: { x: 0, y: 0 },
        });
        this.slider.style.transitionDuration = '0ms';
    };

    onPinchStart = (syntheticEvent) => {
        this.slides[this.index].style.transitionDuration = '0.0s';
        const [p1, p2] = getPointers(syntheticEvent);

        // on pinch start remember the mid point of 2 touch points
        this.pinchStartTouchMidpoint = Point.midpoint(p1, p2);

        // on pinch start remember the distance of 2 touch points
        this.pinchStartTouchPointDist = Point.distance(p1, p2);

        /*
         on pinch start, remember the `origianl zoom factor`
         & `origianl plan translate` before pinching
         */
        const { currentZoomFactor, currentTranslate } = this.getTransform();
        this.pinchStartZoomFactor = currentZoomFactor;
        this.pinchStartTranslate = currentTranslate;
    };

    getTransform = () => {
        const { zoomFactor, translate } = this.transform;
        return {
            currentZoomFactor: zoomFactor,
            currentTranslate: {
                x: translate.x,
                y: translate.y,
            },
        };
    };

    onPinchMove = (syntheticEvent) => {
        // get lastest touch point coordinate
        const [p1, p2] = getPointers(syntheticEvent);

        const pinchCurrentTouchPointDist = Point.distance(p1, p2);

        // delta > 0: enlarge(zoon in), delta < 0: diminish(zoom out)
        const deltaTouchPointDist = pinchCurrentTouchPointDist - this.pinchStartTouchPointDist;

        // update zoom factor
        const newZoomFactor = this.pinchStartZoomFactor + deltaTouchPointDist * 0.01;
        this.zoomContentArea(newZoomFactor);
    };

    zoomContentArea = (zoomFactor) => {
        if (!this.slides[this.index]) {
            return;
        }
        // calculate delta translate needed
        const prevZoomFactor = this.pinchStartZoomFactor;
        const prevTranslate = this.pinchStartTranslate;
        const { innerWidth: containerW, innerHeight: containerH } = window;

        const boundSize = {
            width: containerW,
            height: containerH,
        };

        const prevZoomSize = Size.scale(boundSize, prevZoomFactor);
        const nextZoomSize = Size.scale(boundSize, zoomFactor);

        const prevRectCenterPoint = {
            x: prevZoomSize.width / 2,
            y: prevZoomSize.height / 2,
        };

        const nextRectCenterPoint = {
            x: nextZoomSize.width / 2,
            y: nextZoomSize.height / 2,
        };

        const deltaTranslate = Point.scale(
            Point.offset(prevRectCenterPoint, nextRectCenterPoint),
            1 / (zoomFactor * prevZoomFactor),
        );

        const accumulateTranslate = Point.sum(deltaTranslate, prevTranslate);

        // update zoom scale and corresponding translate
        this.setTransform({
            zoomFactor: truncRound(zoomFactor),
            translate: accumulateTranslate,
        });
    };

    setTransform({
        zoomFactor = this.transform.zoomFactor,
        translate = {
            x: this.transform.translate.x,
            y: this.transform.translate.y,
        },
    } = {}) {
        if (!this.slides[this.index]) {
            return;
        }
        const roundTranslateX = Math.round(translate.x * 1000) / 1000;
        const roundTranslateY = Math.round(translate.y * 1000) / 1000;

        if (zoomFactor < minZoomScale * 0.8) {
            return;
        }

        // update the last transform value
        this.transform.zoomFactor = zoomFactor;
        this.transform.translate.x = roundTranslateX;
        this.transform.translate.y = roundTranslateY;

        // update the transform style
        const styleString = `
        scale(${zoomFactor})
        translate(${roundTranslateX}px, ${roundTranslateY}px)
        translateZ(${0})
      `;

        this.slides[this.index].style.transform = styleString;
        this.slides[this.index].style.webkitTransform = styleString;
    }

    onPinchEnd = () => {
        this.guardZoomAreaScale();
        this.guardZoomAreaTranslate();
    };

    guardZoomAreaScale = () => {
        const { currentZoomFactor } = this.getTransform();
        if (currentZoomFactor > maxZoomScale) {
            this.zoomContentArea(maxZoomScale);
        } else if (currentZoomFactor < minZoomScale) {
            this.zoomContentArea(minZoomScale);
        }
    };

    guardZoomAreaTranslate = () => {
        const { currentZoomFactor, currentTranslate } = this.getTransform();
        const { innerWidth: containerW, innerHeight: containerH } = window;
        const { clientWidth: contentW, clientHeight: contentH } = this.slides[
            this.index
        ];
        if (currentZoomFactor < minZoomScale) {
            return;
        }

        // container size
        const boundSize = {
            width: containerW,
            height: containerH,
        };

        // content size adjusted to zoom factor
        const contentSize = Size.scale(
            {
                width: contentW,
                height: contentH,
            },
            currentZoomFactor,
        );

        const diff = Size.diff(boundSize, contentSize);
        const diffInPoint = Size.toPoint(diff);

        const unitScaleLeftTopPoint = Point.scale(
            diffInPoint,
            1 / (2 * currentZoomFactor),
        );

        const maxLeftTopPoint = Point.boundWithin(
            Point.newOriginPoint(),
            unitScaleLeftTopPoint,
            Point.map(unitScaleLeftTopPoint, truncRound),
        );

        const unitScaleRightBottomPoint = Point.scale(
            diffInPoint,
            1 / currentZoomFactor,
        );

        const maxRightBottomPoint = {
            x: Math.min(unitScaleRightBottomPoint.x, maxLeftTopPoint.x),
            y: Math.min(unitScaleRightBottomPoint.y, maxLeftTopPoint.y),
        };

        const validatePos = Point.boundWithin(
            maxRightBottomPoint,
            currentTranslate,
            maxLeftTopPoint,
        );

        if (!Point.isEqual(validatePos, currentTranslate)) {
            this.panContentArea(validatePos);
        }
    };

    panContentArea = (pos) => {
        this.setTransform({
            translate: pos,
        });
    };

    onPanStart = (syntheticEvent) => {
        const [p1] = getPointers(syntheticEvent);
        const { currentTranslate } = this.getTransform();

        this.panStartPoint = p1;
        this.panStartTranslate = currentTranslate;
    };

    onSliderPan = (e) => {
        const { currentZoomFactor } = this.getTransform();
        if (currentZoomFactor !== 1) {
            return;
        }
        const activeSlide = this.index;
        const { slides } = this;
        this.slider.style.transitionDuration = '0ms';
        const newSliderPosition = window.innerWidth * activeSlide - e.deltaX;
        const newSliderPositionNormalized = Math.min(
            Math.max(newSliderPosition, 0),
            window.innerWidth * (slides.length - 1),
        );
        this.slider.style.transform = `translateX(${-newSliderPosition}px)`;
        if (e.isFinal) {
            this.slider.style.transitionDuration = '300ms';
            let newIndex = Math.round(
                Math.abs(newSliderPositionNormalized) / window.innerWidth,
            );
            if (e.velocityX > SWIPE_VELOCITY) {
                newIndex = this.index - 1;
            }
            if (e.velocityX < -1 * SWIPE_VELOCITY) {
                newIndex = this.index + 1;
            }
            const newIndexNormalized = Math.min(
                Math.max(newIndex, 0),
                slides.length - 1,
            );
            this.setNewIndex(newIndexNormalized);
            this.slider.style.transform = `translateX(${-window.innerWidth
                * newIndexNormalized}px)`;
        }
    };

    onSliderTap = (e) => {
        if (e.type === 'doubletap') {
            this.slides[this.index].style.transitionDuration = '300ms';
            const { currentZoomFactor } = this.getTransform();
            if (currentZoomFactor > minZoomScale) {
                this.setTransform({
                    zoomFactor: minZoomScale,
                    translate: { x: 0, y: 0 },
                });
            } else if (currentZoomFactor === minZoomScale) {
                this.setTransform({
                    zoomFactor: maxZoomScale,
                    translate: {
                        x: -window.innerWidth / 4,
                        y: -window.innerHeight / 4,
                    },
                });
            }
            return;
        }
        if (e.type === 'singletap') {
            this.onClose();
        }
    };

    onBodyTap = (e) => {
        if (e.type === 'doubletap') {
            e.preventDefault();
            return false;
        }
    };

    onPanMove = (syntheticEvent) => {
        const [dragPoint] = getPointers(syntheticEvent);
        const { currentZoomFactor } = this.getTransform();
        const origin = this.panStartPoint;
        if (currentZoomFactor === 1) {
            return;
        }
        const prevTranslate = this.panStartTranslate;

        const dragOffset = Point.offset(dragPoint, origin);
        const adjustedZoomOffset = Point.scale(
            dragOffset,
            1 / currentZoomFactor,
        );
        const nextTranslate = Point.sum(adjustedZoomOffset, prevTranslate);
        this.panContentArea(nextTranslate);
    };

    onPanEnd = () => {
        this.guardZoomAreaTranslate();
    };
}
